import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { useAuth } from 'src/hooks';
import { useProgressStore } from 'src/hooks/useProgressStore';

import { getWidgetList } from 'src/utils/urls';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type TResult<TData = unknown> = {
  Errors?: ApiErrorItem[];
  ErrorId?: string;
  IsSuccess: boolean;
  StatusCode: number;
  Data?: TData;
};

type TError = Error;

type TArguments = {
  id: string;
  saveRequest: Array<string>;
  token: string;
  tokenType: string;
};

type TVariables = {
  saveRequest: Array<string>;
};

async function saveUserSettings({
  id,
  token,
  tokenType,
  saveRequest,
}: TArguments): Promise<TResult> {
  const response = await fetch(getWidgetList, {
    method: 'PUT',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      Id: id,
      InputSettings: {
        'Dashboard-Settings': saveRequest.join(','),
      },
    }),
  });

  let data: TResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useSaveDashboardSettings(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  unknown
> {
  const { token, tokenType, userId } = useAuth();
  const queryClient = useQueryClient();
  const { setLoading } = useProgressStore();

  return useMutation(
    async (params: TVariables) => {
      setLoading('isSaveSettingsLoading', true);

      console.log(
        'Saving dashboard settings with request:',
        params.saveRequest,
      );

      const result = await saveUserSettings({
        id: userId,
        saveRequest: params.saveRequest,
        token,
        tokenType,
      });

      if (result.StatusCode === 302) {
        throw new Error('Expired');
      }

      if (!result.IsSuccess) {
        const errorMessage =
          result.Errors?.map(err => err.Message).join(', ') ||
          'Failed to save dashboard settings';

        throw new Error(errorMessage);
      }

      queryClient.invalidateQueries(['get_dashboard_settings']);

      return result;
    },
    {
      onSettled: () => {
        setLoading('isSaveSettingsLoading', false);
      },
    },
  );
}

export { useSaveDashboardSettings };
